import { type CurrencyCode } from '@/modules/currencies/constants';
import { type Feedback } from '@/modules/feedback/types';
import {
  type DepopShippingStatus as DepopParcelShippingStatus,
  type ListParcel,
  type ManualShippingStatus as ManualParcelShippingStatus,
  type Parcel,
  type ParcelBase,
} from '@/modules/parcels/types';

export enum Role {
  Buyer = 'buyer',
  Seller = 'seller',
}

export type ShippingAddress = {
  name: string;
  address: string;
  address2?: string;
  city: string;
  state?: string;
  postcode?: string;
  country: string;
  email?: string;
};

export enum ReceiptStatus {
  AwaitingShipping = 'awaitingShipping',
  Shipped = 'shipped',
  Delivered = 'delivered',
  ShippingLabelExpired = 'shippingLabelExpired',
  Unknown = 'unknown',
  Failed = 'failed',
  InTransit = 'inTransit',
  Returned = 'returned',
  Cancelled = 'cancelled',
}

export enum ShippingStatus {
  Shipped = 'shipped',
  AwaitingShipping = 'awaitingShipping',
  All = '',
}

export enum SoldItemsShippingStatus {
  All = '',
  ToShip = 'awaitingShipping',
  InTransit = 'shipped',
  Cancelled = 'cancelled',
  Completed = 'complete',
}

export enum PaymentProvider {
  PayPal = 'paypal',
  Wallet = 'wallet',
  Stripe = 'stripe',
  Klarna = 'klarna',
}

export enum ListReceiptShippingProviderType {
  Depop = 'depop',
  Manual = 'manual',
}

export type RefundDetails = {
  isRefundable: boolean;
  refundDate?: string;
  buyerRefundAmount?: string;
};

type ReceiptBase<U, T> = {
  purchaseId: number;
  transactionId: number;
  buyer: U;
  seller: U;
  shippingAddress: ShippingAddress;
  shipFromAddressId?: number;
  soldTimestamp: string;
  shippingStatus: ShippingStatus;
  cancellationDetails?: {
    reason: string;
  };
} & T;

export declare namespace ListReceipt {
  type Item = {
    pictureUrl: string;
    productId: number;
    description: string;
  };

  type User = {
    id: number;
    username: string;
  };

  type Receipt = Omit<
    ReceiptBase<
      User,
      {
        buyerPaidAmount: string;
        buyerPaymentProvider: PaymentProvider;
        buyerRefundAmount?: string;
        currency: CurrencyCode;
        hasBoostingFee?: boolean;
        isBundle?: boolean;
        isCancelled?: boolean;
        items: Item[];
        lineItems?: { id: number; repopAvailable: boolean }[];
        parcels: ListParcel.AnyParcel[];
        seller: User;
        sellerPaidAmount?: string;
      }
    >,
    'shippingAddress'
  > & { shippingAddress?: ShippingAddress };

  type ShippingProviderBase = ParcelBase<
    ManualParcelShippingStatus | DepopParcelShippingStatus,
    {
      type: ListReceiptShippingProviderType;
      providerName?: string;
    }
  >;

  export type ShippingProvider = Omit<ShippingProviderBase, 'shippingProvider'>;

  type TransformedReceipt = Pick<
    Receipt,
    | 'purchaseId'
    | 'transactionId'
    | 'soldTimestamp'
    | 'shippingStatus'
    | 'parcels'
    | 'hasBoostingFee'
    | 'isBundle'
    | 'isCancelled'
    | 'lineItems'
    | 'cancellationDetails'
    | 'buyerRefundAmount'
    | 'currency'
    | 'items'
    | 'shipFromAddressId'
  > & {
    urls: Array<string>;
    productIds: Array<number>;
    itemCount: number;
    buyerInfo: {
      id: number;
      username: string;
      location: {
        address: string;
        address2?: string;
        city: string;
        postcode: string;
        formattedAddress: string;
        state: string;
        country: string;
      };
    };
    sellerInfo: {
      id: number;
      username: string;
    };
    paymentInfo: {
      buyerPaidAmount: string;
      buyerPaidAmountPlain: string;
      provider: PaymentProvider;
      sellerPaidAmount?: string;
    };
    shippingProviders: ShippingProvider[];
    depopShipping?: ShippingProvider;
    manualShipping?: ShippingProvider;
  };
}

export enum PayoutDescriptionStatus {
  Paid = 'paid',
  Pending = 'pending',
  InTransit = 'inTransit',
  Cancelled = 'cancelled',
  Failed = 'failed',
}

export declare namespace Receipt {
  type User = ListReceipt.User & {
    firstName?: string;
    lastName?: string;
    pictureUrl?: string;
    isActive: boolean;
  };

  type Item = ListReceipt.Item & {
    slug?: string;
    brand?: string;
    itemAmount: string;
    undiscountedAmount?: string;
    itemCurrency: CurrencyCode;
    description: string;
    size?: string;
    hasDepopShipping: boolean;
    boostingFee?: FeeAmount;
    sku?: string;
  };

  type ReceiptFeedback = {
    needed: boolean;
    leftByBuyer?: Feedback;
    leftBySeller?: Feedback;
  };

  type PayoutDescription = {
    id: number;
    status: PayoutDescriptionStatus;
    arrivalDate: string;
    amount: string;
    currency: CurrencyCode;
  };

  type PaymentDetails = {
    currency: CurrencyCode;
    itemsAmount: string;
    undiscountedItemsAmount?: string;
    paymentFee?: string;
    depopFee?: string;
    boostingFee?: FeeAmount;
    buyerPaidAmount: string;
    sellerReceivedAmount: string;
    payoutDescription?: PayoutDescription;
    payoutEstimatedDate?: string;
    buyerPaymentProvider: PaymentProvider;
    shippingAmount: string;
    buyerShippingAmount: string;
    sellerShippingAmount: string;
    taxSummary?: {
      totalTaxAmount: string;
      shipToLocation: string;
    };
    buyerFee: FeeAmount;
    sellerFee: FeeAmount;
  };

  type FeeAmount = {
    amount: string;
    originalAmount?: string;
  };

  type LineItem = {
    id: number;
    repopAvailable: boolean;
  };

  type Receipt = ReceiptBase<
    User,
    {
      receiptStatus: ReceiptStatus;
      refundDetails?: RefundDetails;
      items: Item[];
      parcels: Parcel.AnyParcel[];
      feedback?: ReceiptFeedback;
      paymentDetails: PaymentDetails;
      isBundle?: boolean;
      lineItems?: LineItem[];
    }
  >;
}

export type Meta = {
  cursor: string | null;
  end: boolean;
};

export enum ReceiptType {
  Buyer = 'buyer',
  Seller = 'seller',
}

export type GetReceiptsResponse = {
  meta: Meta;
  receipts: ListReceipt.Receipt[];
};

export type PaymentDetailLine = {
  label: string;
  originalPrice?: string;
  price: string;
};

export type SoldCarouselItem = {
  purchaseId: number;
  pictureUrl: string;
  description: string;
  soldTimestamp: string;
  shippingStatus: string;
  itemsCount: number;
  parcelCount: {
    shipped: number;
    total: number;
  };
  paymentInfo: {
    amount: string;
    isDepopPayment: boolean;
    isKlarnaPayment: boolean;
  };
  shippedAt?: string;
  hasBoostingFee?: boolean;
};

export type FetchReceiptsArgs = {
  cursor: string | null;
  limit?: number;
  role: Role;
  shippingStatus?: SoldItemsShippingStatus;
  sku?: string;
  userId?: number;
};
